/**
 * FeatureRequirementsPanel - Reusable panel for Feature Requirements/Acceptance Criteria
 * 
 * Used in:
 * - FeatureDetailModal (edit mode)
 * - FeatureMergeModal (source view + merged form)
 * 
 * Props:
 * - requirements: array of { id, text, is_completed } objects
 * - onAdd: (text) => void
 * - onUpdate: (id, field, value) => void
 * - onDelete: (id) => void
 * - onToggleComplete: (id, isCompleted) => void
 * - isEditing: boolean (controls whether requirements can be edited)
 * - colors: theme colors from useSafeMode
 * - mode: 'edit' | 'create' | 'view'
 */

import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { Plus, Trash2, Check, Circle, GripVertical } from 'lucide-react';

// Generate unique requirement ID
export const generateRequirementId = () => `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const FeatureRequirementsPanel = ({
  requirements = [],
  onAdd,
  onUpdate,
  onDelete,
  onToggleComplete,
  isEditing = true,
  colors,
  mode = 'edit',
}) => {
  const [newRequirement, setNewRequirement] = useState('');

  const handleAddRequirement = () => {
    if (!newRequirement.trim() || !onAdd) return;
    onAdd(newRequirement.trim());
    setNewRequirement('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddRequirement();
    }
  };

  // Calculate progress
  const completedCount = requirements.filter(r => r.is_completed).length;
  const totalCount = requirements.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Label className={cn("flex items-center gap-2", colors.textPrimary)}>
          <Check className="w-4 h-4" />
          Acceptance Criteria ({totalCount})
        </Label>
        {totalCount > 0 && (
          <span className={cn("text-sm", colors.textMuted)}>
            {completedCount}/{totalCount} complete ({progressPercent}%)
          </span>
        )}
      </div>

      {/* Progress Bar - Only show when there are requirements */}
      {totalCount > 0 && (
        <div className={cn("w-full h-2 rounded-full overflow-hidden", "bg-gray-200 dark:bg-gray-700")}>
          <div 
            className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}

      {/* Add New Requirement - Only in edit mode */}
      {isEditing && mode !== 'view' && onAdd && (
        <div className="flex items-center gap-2">
          <Input
            value={newRequirement}
            onChange={(e) => setNewRequirement(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Add acceptance criterion..."
            className={cn("flex-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
            data-testid="new-requirement-input"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={handleAddRequirement}
            disabled={!newRequirement.trim()}
            className={cn(colors.borderPrimary)}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Requirements List */}
      {totalCount === 0 ? (
        <div className={cn("text-center py-8 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
          <Check className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
          <p className={cn("text-sm", colors.textMuted)}>No acceptance criteria defined yet</p>
          {isEditing && mode !== 'view' && (
            <p className={cn("text-xs mt-1", colors.textMuted)}>
              Add criteria to define what "done" means for this feature
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {requirements.map((req, index) => (
            <div
              key={req.id || index}
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border group transition-colors",
                req.is_completed 
                  ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800" 
                  : cn(colors.bgTertiary, colors.borderPrimary),
                "hover:shadow-sm"
              )}
              data-testid={`requirement-${req.id || index}`}
            >
              {/* Drag Handle - Only in edit mode */}
              {isEditing && mode !== 'view' && (
                <GripVertical className={cn(
                  "w-4 h-4 mt-0.5 cursor-grab opacity-0 group-hover:opacity-50 transition-opacity",
                  colors.textMuted
                )} />
              )}
              
              {/* Completion Toggle */}
              <button
                onClick={() => onToggleComplete?.(req.id, !req.is_completed)}
                disabled={mode === 'view'}
                className={cn(
                  "flex-shrink-0 mt-0.5 transition-colors",
                  mode === 'view' && "cursor-default"
                )}
              >
                {req.is_completed ? (
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                ) : (
                  <Circle className={cn("w-5 h-5", colors.textMuted, "hover:text-green-500")} />
                )}
              </button>
              
              {/* Requirement Text */}
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "text-sm",
                  req.is_completed 
                    ? "text-green-700 dark:text-green-400 line-through" 
                    : colors.textPrimary
                )}>
                  {req.text}
                </p>
              </div>
              
              {/* Delete Button - Only in edit mode */}
              {isEditing && mode !== 'view' && onDelete && (
                <button
                  onClick={() => onDelete(req.id)}
                  className={cn(
                    "flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity",
                    "text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30"
                  )}
                  title="Delete requirement"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeatureRequirementsPanel;
